import {
  Box, Typography, Chip, Tooltip, Drawer, IconButton, CircularProgress, Skeleton,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  CheckCircle, InfoOutlined, ReportProblem, DangerousRounded,
  Close, Warning, TrendingUp, TrendingDown, TrendingFlat,
  EmojiEvents, WorkspacePremium, Star, Lock, GppBad, GppMaybe,
} from '@mui/icons-material';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import {
  getTier, getStyle, getFailList, getFailLabel,
  fmtDec, PILLAR_LEVEL_COLOR, riskColor, scoreColor,
} from './constants';
import { BreakoutBarBadge } from './primitives';

const TickerDrawer = ({
  drawerTicker, setDrawerTicker,
  tickerHistory, historyLoading,
  drawerExporting,
  drawerExportRef, drawerBodyRef,
  handleExportDrawerImage,
  total, sn, t,
}) => (
  <Drawer
    anchor="right"
    open={!!drawerTicker}
    onClose={() => setDrawerTicker(null)}
    PaperProps={{ sx: { width: { xs: '100%', sm: 440 }, p: 0 } }}
  >
    <Box ref={drawerExportRef} sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#fff' }}>
      {/* Header */}
      {(() => {
        const latest = tickerHistory?.latest;
        const tier = getTier(latest?.strength_tier);
        return (
          <Box sx={{
            px: 2, py: 1.5,
            background: `linear-gradient(135deg, ${alpha(tier.color, 0.08)} 0%, #ffffff 100%)`,
            borderBottom: '1px solid #e2e8f0',
            display: 'flex', alignItems: 'center', gap: 1.5,
          }}>
            <Box sx={{
              width: 44, height: 44, borderRadius: 1.5,
              bgcolor: tier.color, color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 800, fontSize: '0.95rem', fontFamily: 'monospace',
              letterSpacing: '0.05em', flexShrink: 0,
              boxShadow: `0 4px 12px ${alpha(tier.color, 0.3)}`,
            }}>
              {drawerTicker}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.25, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                {tickerHistory?.company_name || drawerTicker}
              </Typography>
              {tickerHistory?.sector && (
                <Chip
                  label={sn(tickerHistory.sector)}
                  size="small"
                  sx={{
                    height: 16, fontSize: '0.55rem', fontWeight: 600,
                    bgcolor: alpha(tier.color, 0.1), color: tier.color,
                    mt: 0.4,
                    '& .MuiChip-label': { px: 0.7 },
                  }}
                />
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }} data-html2canvas-ignore="true">
              <Tooltip title={t('stockStrength.drawer.exportImageTooltip', 'Tải ảnh PNG')} placement="top">
                <span>
                  <IconButton
                    size="small"
                    onClick={handleExportDrawerImage}
                    disabled={drawerExporting || historyLoading || !tickerHistory?.latest}
                    sx={{ color: '#64748b', '&:hover': { color: '#0f172a', bgcolor: '#f1f5f9' } }}
                  >
                    {drawerExporting
                      ? <CircularProgress size={14} color="inherit" />
                      : <DownloadRoundedIcon sx={{ fontSize: 18 }} />}
                  </IconButton>
                </span>
              </Tooltip>
              <IconButton size="small" onClick={() => setDrawerTicker(null)} sx={{ color: '#64748b' }}>
                <Close fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        );
      })()}

      {/* Body */}
      <Box ref={drawerBodyRef} sx={{ p: 2, overflowY: 'auto', flex: 1 }}>
        {historyLoading ? (
          <Box>
            <Box sx={{ mx: -2, mt: -2, mb: 2, px: 2, py: 1.75, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    <Skeleton variant="text" width={140} height={28} animation="wave" />
                    <Skeleton variant="rounded" width={70} height={22} animation="wave" sx={{ borderRadius: 1 }} />
                    <Skeleton variant="text" width={50} height={16} animation="wave" />
                  </Box>
                  <Skeleton variant="rounded" height={6} animation="wave" sx={{ mt: 0.9, borderRadius: 3 }} />
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Skeleton variant="text" width={56} height={22} animation="wave" sx={{ ml: 'auto' }} />
                  <Skeleton variant="text" width={48} height={12} animation="wave" sx={{ ml: 'auto' }} />
                  <Skeleton variant="text" width={64} height={14} animation="wave" sx={{ ml: 'auto', mt: 0.3 }} />
                </Box>
              </Box>
              <Box sx={{ mt: 1.2, display: 'flex', gap: 0.5 }}>
                <Skeleton variant="rounded" width={80} height={24} animation="wave" sx={{ borderRadius: 2 }} />
                <Skeleton variant="rounded" width={90} height={24} animation="wave" sx={{ borderRadius: 2 }} />
              </Box>
              <Skeleton variant="rounded" height={42} animation="wave" sx={{ mt: 1.2, borderRadius: 1 }} />
              <Skeleton variant="text" width={220} height={14} animation="wave" sx={{ mt: 0.3 }} />
            </Box>
            <Box sx={{ mb: 2, p: 1.4, borderRadius: 1.5, bgcolor: '#f1f5f9', border: '1px solid #e2e8f0' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.6 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                  <Skeleton variant="circular" width={18} height={18} animation="wave" />
                  <Skeleton variant="text" width={170} height={18} animation="wave" />
                </Box>
                <Skeleton variant="rounded" width={90} height={22} animation="wave" sx={{ borderRadius: 1 }} />
              </Box>
              <Skeleton variant="text" height={14} animation="wave" />
              <Skeleton variant="text" width="70%" height={14} animation="wave" />
            </Box>
            <Box sx={{ mb: 2 }}>
              <Skeleton variant="text" width={200} height={12} animation="wave" sx={{ mb: 0.8 }} />
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1.2, mb: 1.2 }}>
                {[0, 1, 2, 3].map(i => (
                  <Box key={i}>
                    <Skeleton variant="text" width="60%" height={10} animation="wave" />
                    <Skeleton variant="text" width="80%" height={20} animation="wave" />
                    <Skeleton variant="text" width="70%" height={10} animation="wave" />
                  </Box>
                ))}
              </Box>
              <Box sx={{ pt: 1.2, borderTop: '1px dashed #e2e8f0' }}>
                <Skeleton variant="text" width={160} height={12} animation="wave" sx={{ mb: 0.6 }} />
                {[0, 1, 2].map(i => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.9, mb: 0.5 }}>
                    <Skeleton variant="circular" width={22} height={22} animation="wave" />
                    <Skeleton variant="text" width={`${60 + i * 8}%`} height={14} animation="wave" />
                  </Box>
                ))}
              </Box>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Skeleton variant="text" width={140} height={12} animation="wave" sx={{ mb: 0.8 }} />
              {[0, 1, 2, 3, 4].map(i => (
                <Box key={i} sx={{ display: 'grid', gridTemplateColumns: '78px 1fr 70px', alignItems: 'center', gap: 1, mb: 0.55 }}>
                  <Skeleton variant="text" width={60} height={14} animation="wave" />
                  <Skeleton variant="rounded" height={7} animation="wave" sx={{ borderRadius: 4 }} />
                  <Skeleton variant="text" width={50} height={14} animation="wave" sx={{ ml: 'auto' }} />
                </Box>
              ))}
              <Box sx={{ display: 'grid', gridTemplateColumns: '78px 1fr 70px', alignItems: 'center', gap: 1, mt: 0.8, pt: 0.6, borderTop: '1px dashed #e2e8f0' }}>
                <Skeleton variant="text" width={60} height={14} animation="wave" />
                <Skeleton variant="rounded" height={7} animation="wave" sx={{ borderRadius: 4 }} />
                <Skeleton variant="text" width={50} height={14} animation="wave" sx={{ ml: 'auto' }} />
              </Box>
            </Box>
            <Box>
              <Skeleton variant="text" width={160} height={12} animation="wave" sx={{ mb: 0.8 }} />
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <Skeleton variant="rounded" width={140} height={26} animation="wave" sx={{ borderRadius: 1 }} />
                <Skeleton variant="rounded" width={150} height={26} animation="wave" sx={{ borderRadius: 1 }} />
              </Box>
              <Box sx={{ px: 1.2, py: 0.9, bgcolor: '#fafafa', border: '1px solid #f1f5f9', borderRadius: 1.5 }}>
                <Skeleton variant="text" width={120} height={12} animation="wave" sx={{ mb: 0.6 }} />
                {[0, 1, 2].map(i => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.7, py: 0.3 }}>
                    <Skeleton variant="text" width={70} height={12} animation="wave" />
                    <Skeleton variant="circular" width={6} height={6} animation="wave" />
                    <Skeleton variant="text" width={`${40 + i * 10}%`} height={14} animation="wave" />
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        ) : tickerHistory?.error ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography sx={{ fontSize: '0.85rem', color: '#dc2626', fontWeight: 600 }}>
              {tickerHistory.error}
            </Typography>
            <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', mt: 0.5 }}>
              {t('stockStrength.drawer.notScored', 'Mã chưa được tính sức mạnh trong kỳ này.')}
            </Typography>
          </Box>
        ) : tickerHistory?.latest ? (() => {
          const latest = tickerHistory.latest;
          const tier = getTier(latest.strength_tier);
          const c = latest.components || {};
          const fails = getFailList(latest);
          const stats = tickerHistory.stats || {};
          const priceSnap = tickerHistory.price_snapshot;
          const sectorCtx = tickerHistory.sector_context;
          const interp = tickerHistory.interpretation || {};
          const verdictMeta = interp.verdict || { key: 'NEUTRAL', tone: 'neutral', action: 'NEUTRAL' };
          const sparkTrendMeta = interp.spark_trend || { key: 'flat', icon: 'flat', tone: 'neutral' };
          const pillarsMeta = interp.pillars || [];
          const riskMeta = interp.risk || {};
          const activeRedFlagKeys = interp.active_red_flags || [];
          const milestones = interp.milestones || [];
          const historySummary = interp.history_summary || {};

          const redFlagColorMap = {
            exhausted: '#f59e0b', distribution: '#ef4444', climaxTop: '#dc2626',
            compoundRisk: '#b91c1c', controlled: '#f97316', illiquid: '#64748b',
          };
          const activeFlags = activeRedFlagKeys.map(k => ({ key: k, color: redFlagColorMap[k] || '#64748b' }));

          const compRows = pillarsMeta.map(p => ({
            key: p.key,
            label: t(`stockStrength.table.${p.key}`),
            val: p.value,
            levelKey: p.level_key,
          }));
          const sparkPoints = (tickerHistory.history || []).slice().reverse()
            .map((h, i) => ({ x: i, y: h.strength_score ?? null, d: h.trade_date }))
            .filter(p => p.y != null);

          const SectionLabel = ({ children, right }) => (
            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.8 }}>
              <Typography sx={{ fontSize: '0.6rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 }}>
                {children}
              </Typography>
              {right && <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8' }}>{right}</Typography>}
            </Box>
          );
          const StatCell = ({ label, value, sub, hint, color = '#0f172a' }) => (
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.5rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700, whiteSpace: 'nowrap' }}>{label}</Typography>
              <Typography sx={{ fontSize: '0.92rem', fontWeight: 800, fontFamily: 'monospace', color, lineHeight: 1.2, whiteSpace: 'nowrap' }}>{value}</Typography>
              {hint && <Typography sx={{ fontSize: '0.55rem', color, fontWeight: 600, mt: 0.1 }}>{hint}</Typography>}
              {sub && <Typography sx={{ fontSize: '0.55rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>{sub}</Typography>}
            </Box>
          );

          const verdict = {
            key: verdictMeta.key,
            tone: verdictMeta.tone,
            action: verdictMeta.action,
            title: t(`stockStrength.drawer.verdict.${verdictMeta.key}.title`),
            message: t(`stockStrength.drawer.verdict.${verdictMeta.key}.message`),
          };
          const verdictColors = {
            good:    { bg: '#dcfce7', border: '#86efac', text: '#15803d', Icon: CheckCircle },
            neutral: { bg: '#f1f5f9', border: '#cbd5e1', text: '#475569', Icon: InfoOutlined },
            warn:    { bg: '#fef3c7', border: '#fcd34d', text: '#92400e', Icon: ReportProblem },
            danger:  { bg: '#fee2e2', border: '#fca5a5', text: '#b91c1c', Icon: DangerousRounded },
          }[verdict.tone];
          const actionLabel = t(`stockStrength.drawer.verdictAction.${verdict.action}`);

          const resolveHint = (key, group) => key ? t(`stockStrength.drawer.hint.${group}.${key}`) : null;

          return (
            <>
              {/* ═══ HERO ═══ */}
              <Box sx={{
                mx: -2, mt: -2, mb: 2, px: 2, py: 1.75,
                background: `linear-gradient(180deg, ${alpha(tier.color, 0.07)} 0%, ${alpha(tier.color, 0.02)} 100%)`,
                borderBottom: '1px solid #e2e8f0',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {(() => {
                      const headlineKey = interp.headline_key || 'average';
                      const headline = t(`stockStrength.drawer.headline.${headlineKey}`);
                      const s = latest.strength_score || 0;
                      return (
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.8, flexWrap: 'wrap' }}>
                          <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, lineHeight: 1, color: tier.color }}>
                            {headline}
                          </Typography>
                          {latest.strength_tier && latest.strength_tier !== 'REST' && (
                            <Chip label={`${tier.icon} ${t('stockStrength.tiers.' + tier.labelKey)}`} size="small" sx={{
                              height: 22, fontSize: '0.65rem', fontWeight: 700,
                              bgcolor: alpha(tier.color, 0.12), color: tier.color,
                              border: `1px solid ${alpha(tier.color, 0.3)}`,
                            }} />
                          )}
                          <Tooltip arrow title={t('stockStrength.drawer.scoreTooltip')}>
                            <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 600, fontFamily: 'monospace', cursor: 'help' }}>
                              {fmtDec(s, 0)}/100
                            </Typography>
                          </Tooltip>
                        </Box>
                      );
                    })()}
                    <Box sx={{ mt: 0.7, height: 6, bgcolor: alpha(tier.color, 0.1), borderRadius: 3, overflow: 'hidden' }}>
                      <Box sx={{ width: `${Math.max(0, Math.min(100, latest.strength_score || 0))}%`, height: '100%', bgcolor: tier.color, transition: 'width 0.4s ease' }} />
                    </Box>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography sx={{ fontSize: '0.92rem', fontWeight: 800, color: '#0f172a', fontFamily: 'monospace', lineHeight: 1.1 }}>
                      #{latest.strength_rank || '—'}
                      <Typography component="span" sx={{ fontSize: '0.6rem', color: '#94a3b8', fontWeight: 500, ml: 0.3 }}>/ {total}</Typography>
                    </Typography>
                    <Typography sx={{ fontSize: '0.55rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700 }}>
                      {t('stockStrength.drawer.rank', 'Xếp hạng')}
                    </Typography>
                    {latest.criteria_passed != null && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, justifyContent: 'flex-end', mt: 0.3 }}>
                        <CheckCircle sx={{ fontSize: 11, color: latest.criteria_passed >= 4 ? '#10b981' : latest.criteria_passed >= 2 ? '#f59e0b' : '#94a3b8' }} />
                        <Typography sx={{ fontSize: '0.6rem', color: '#64748b', fontWeight: 600 }}>
                          {t('stockStrength.drawer.criteriaPassed', { n: latest.criteria_passed })}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>

                {((stats.tier_streak_days > 0 && latest.strength_tier && latest.strength_tier !== 'REST')
                  || latest.persistent_leader || latest.breakout_bar_flag) && (
                  <Box sx={{ mt: 1.2, display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    {stats.tier_streak_days > 0 && latest.strength_tier && latest.strength_tier !== 'REST' && (
                      <Chip size="small" label={t('stockStrength.drawer.tierStreak', { n: stats.tier_streak_days })}
                        sx={{ height: 24, fontSize: '0.65rem', fontWeight: 700, bgcolor: alpha(tier.color, 0.1), color: tier.color, '& .MuiChip-label': { px: 0.95 } }} />
                    )}
                    {latest.persistent_leader && (
                      <Chip size="small" label={t('stockStrength.tiers.persistent')}
                        sx={{ height: 24, fontSize: '0.65rem', fontWeight: 700, bgcolor: alpha('#8b5cf6', 0.12), color: '#6d28d9', '& .MuiChip-label': { px: 0.95 } }} />
                    )}
                    {latest.breakout_bar_flag && <BreakoutBarBadge t={t} />}
                  </Box>
                )}

                {sparkPoints.length > 2 && (() => {
                  const W = 400, H = 42, PAD = 2;
                  const ys = sparkPoints.map(p => p.y);
                  const minY = Math.min(...ys), maxY = Math.max(...ys);
                  const span = Math.max(1, maxY - minY);
                  const xStep = (W - PAD * 2) / Math.max(1, sparkPoints.length - 1);
                  const pts = sparkPoints.map((p, i) => {
                    const x = PAD + i * xStep;
                    const y = H - PAD - ((p.y - minY) / span) * (H - PAD * 2);
                    return `${x},${y}`;
                  }).join(' ');
                  const lastPt = sparkPoints[sparkPoints.length - 1];
                  const lastY = H - PAD - ((lastPt.y - minY) / span) * (H - PAD * 2);
                  const lastX = PAD + (sparkPoints.length - 1) * xStep;
                  return (
                    <Box sx={{ mt: 1.2 }}>
                      <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ display: 'block' }}>
                        <polyline fill={alpha(tier.color, 0.12)} stroke="none" points={`${PAD},${H - PAD} ${pts} ${lastX},${H - PAD}`} />
                        <polyline fill="none" stroke={tier.color} strokeWidth="1.5" points={pts} />
                        <circle cx={lastX} cy={lastY} r="2.5" fill={tier.color} />
                      </svg>
                      {(() => {
                        const trendKey = sparkTrendMeta.key || 'flat';
                        const trendIconKey = sparkTrendMeta.icon || 'flat';
                        const trendTone = sparkTrendMeta.tone || 'neutral';
                        const TrendIcn = trendIconKey === 'up' ? TrendingUp : trendIconKey === 'down' ? TrendingDown : TrendingFlat;
                        const trendCol = trendTone === 'positive' ? '#10b981'
                          : trendTone === 'negative' ? '#ef4444'
                          : trendTone === 'danger' ? '#dc2626'
                          : '#64748b';
                        return (
                          <Tooltip arrow title={t('stockStrength.drawer.spark.rangeTooltip', { min: fmtDec(stats.score_min, 0), max: fmtDec(stats.score_max, 0), avg: fmtDec(stats.score_avg, 0) })}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3, cursor: 'help' }}>
                              <TrendIcn sx={{ fontSize: 13, color: trendCol }} />
                              <Typography sx={{ fontSize: '0.62rem', color: trendCol, fontWeight: 600 }}>
                                {t(`stockStrength.drawer.spark.trend.${trendKey}`)}
                              </Typography>
                            </Box>
                          </Tooltip>
                        );
                      })()}
                    </Box>
                  );
                })()}
              </Box>

              {/* ═══ VERDICT ═══ */}
              <Box sx={{
                mb: 2, p: 1.4, borderRadius: 1.5,
                bgcolor: verdictColors.bg,
                border: `1px solid ${verdictColors.border}`,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                    {(() => { const VIcon = verdictColors.Icon; return <VIcon sx={{ fontSize: 18, color: verdictColors.text }} />; })()}
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, color: verdictColors.text, lineHeight: 1.2 }}>
                      {verdict.title}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={actionLabel}
                    sx={{
                      height: 22, fontSize: '0.6rem', fontWeight: 800, letterSpacing: '0.04em',
                      bgcolor: verdictColors.text, color: '#fff',
                      '& .MuiChip-label': { px: 0.9 },
                    }}
                  />
                </Box>
                <Typography sx={{ fontSize: '0.72rem', color: verdictColors.text, lineHeight: 1.45, opacity: 0.92 }}>
                  {verdict.message}
                </Typography>
              </Box>

              {/* ═══ TRADE STYLE + AUDIT ═══ */}
              {latest.trade_style && latest.trade_style !== 'NONE' && (() => {
                const styleMeta = getStyle(latest.trade_style);
                const SIcon = styleMeta.Icon;
                const auditParts = (latest.audit_reason || '').split(/[•;]/).map(s => s.trim()).filter(Boolean);
                return (
                  <Box sx={{
                    mb: 2, p: 1.4, borderRadius: 1.5,
                    bgcolor: alpha(styleMeta.color, 0.06),
                    border: `1px solid ${alpha(styleMeta.color, 0.3)}`,
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, mb: 0.7 }}>
                      {SIcon && <SIcon sx={{ fontSize: 18, color: styleMeta.color }} />}
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: styleMeta.color, letterSpacing: 0.3 }}>
                        {styleMeta.label}
                      </Typography>
                      <Typography sx={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 600 }}>
                        — {t('stockStrength.tradeStyle.' + latest.trade_style + '.short', styleMeta.short)}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.7rem', color: '#475569', mb: 0.7, lineHeight: 1.5, fontStyle: 'italic' }}>
                      {t('stockStrength.tradeStyle.' + latest.trade_style + '.tip', styleMeta.tip)}
                    </Typography>
                    {auditParts.length > 0 && (
                      <Box sx={{ pt: 0.7, borderTop: `1px dashed ${alpha(styleMeta.color, 0.25)}` }}>
                        <Typography sx={{ fontSize: '0.55rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700, mb: 0.5 }}>
                          {t('stockStrength.actionTable.whyAssigned')}
                        </Typography>
                        {auditParts.slice(0, 6).map((p, i) => (
                          <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.6, mb: 0.3 }}>
                            <CheckCircle sx={{ fontSize: 12, color: styleMeta.color, mt: 0.2, flexShrink: 0 }} />
                            <Typography sx={{ fontSize: '0.7rem', color: '#1e293b', lineHeight: 1.4 }}>{p}</Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                );
              })()}

              {/* ═══ RED FLAGS ═══ */}
              {activeFlags.length > 0 && (
                <Box sx={{
                  mx: -2, mb: 2, px: 2, py: 1.2,
                  bgcolor: alpha('#fee2e2', 0.5),
                  borderTop: `1px solid ${alpha('#ef4444', 0.2)}`,
                  borderBottom: `1px solid ${alpha('#ef4444', 0.2)}`,
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.6 }}>
                    <Warning sx={{ fontSize: 14, color: '#b91c1c' }} />
                    <Typography sx={{ fontSize: '0.62rem', color: '#b91c1c', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 800 }}>
                      {t('stockStrength.drawer.redFlags.section')}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {activeFlags.map(f => (
                      <Tooltip key={f.key} title={t(`stockStrength.drawer.redFlags.${f.key}Tip`)} arrow>
                        <Chip
                          size="small"
                          label={t(`stockStrength.drawer.redFlags.${f.key}`)}
                          sx={{ height: 22, fontSize: '0.62rem', fontWeight: 700, bgcolor: alpha(f.color, 0.15), color: f.color, border: `1px solid ${alpha(f.color, 0.35)}` }}
                        />
                      </Tooltip>
                    ))}
                  </Box>
                </Box>
              )}

              {/* ═══ KEY METRICS ═══ */}
              {(priceSnap || sectorCtx) && (
                <Box sx={{ mb: 2 }}>
                  <SectionLabel right={priceSnap?.trade_date}>
                    {t('stockStrength.drawer.priceSnapshot')}
                  </SectionLabel>
                  {priceSnap && (
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1.2, mb: sectorCtx?.sector_avg != null ? 1.2 : 0 }}>
                      <StatCell label={t('stockStrength.drawer.price')} value={priceSnap.close ? (priceSnap.close / 1000).toFixed(2) : '—'} />
                      <StatCell
                        label={t('stockStrength.drawer.change')}
                        value={priceSnap.change_pct != null ? `${priceSnap.change_pct > 0 ? '+' : ''}${priceSnap.change_pct.toFixed(2)}%` : '—'}
                        hint={resolveHint(priceSnap.change_pct_hint_key, 'changePct')}
                        color={(priceSnap.change_pct ?? 0) > 0 ? '#10b981' : (priceSnap.change_pct ?? 0) < 0 ? '#ef4444' : '#94a3b8'}
                      />
                      <StatCell
                        label={t('stockStrength.drawer.volRatio')}
                        value={priceSnap.volume_ratio != null ? `${priceSnap.volume_ratio.toFixed(2)}×` : '—'}
                        hint={resolveHint(priceSnap.vol_ratio_hint_key, 'volRatio')}
                        color={(priceSnap.volume_ratio ?? 0) >= 1.5 ? '#10b981' : (priceSnap.volume_ratio ?? 0) < 0.7 ? '#ef4444' : '#475569'}
                      />
                      <StatCell
                        label="20D"
                        value={priceSnap.dist_to_high_pct != null ? `${priceSnap.dist_to_high_pct.toFixed(1)}%` : '—'}
                        hint={resolveHint(priceSnap.dist_high_hint_key, 'distHigh')}
                        color={(priceSnap.dist_to_high_pct ?? 0) > -2 ? '#10b981' : '#475569'}
                      />
                    </Box>
                  )}
                  {sectorCtx && sectorCtx.sector_avg != null && (() => {
                    const ICON_MAP = {
                      trophy: EmojiEvents, premium: WorkspacePremium, star: Star, warning: Warning,
                      up: TrendingUp, flat: TrendingFlat, down: TrendingDown,
                      lock: Lock, gppBad: GppBad, gppMaybe: GppMaybe,
                    };
                    const TONE_COLOR = {
                      positive: '#10b981', good: '#10b981', neutral: '#64748b',
                      info: '#0ea5e9', warn: '#f59e0b', negative: '#ef4444', danger: '#dc2626',
                    };
                    const toLine = (meta, group) => {
                      if (!meta || !meta.key) return null;
                      return {
                        text: t(`stockStrength.drawer.sector.${group}.${meta.key}`, meta.params || {}),
                        color: TONE_COLOR[meta.tone] || '#64748b',
                        Icon: ICON_MAP[meta.icon] || TrendingFlat,
                      };
                    };
                    const lines = [
                      toLine(sectorCtx.rank_text, 'rank'),
                      toLine(sectorCtx.vs_text, 'vs'),
                      toLine(sectorCtx.manip_text, 'manip'),
                    ].filter(Boolean);
                    return (
                      <Box sx={{ pt: priceSnap ? 1.2 : 0, borderTop: priceSnap ? '1px dashed #e2e8f0' : 'none' }}>
                        <Typography sx={{ fontSize: '0.55rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700, mb: 0.6 }}>
                          {t('stockStrength.drawer.sectorContext')} · {sectorCtx.sector || '—'}
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {lines.map((ln, i) => {
                            const Icon = ln.Icon;
                            return (
                              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.9 }}>
                                <Box sx={{
                                  width: 22, height: 22, borderRadius: '50%',
                                  bgcolor: alpha(ln.color, 0.12),
                                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                }}>
                                  <Icon sx={{ fontSize: 14, color: ln.color }} />
                                </Box>
                                <Typography sx={{ fontSize: '0.74rem', color: ln.color, fontWeight: 600, lineHeight: 1.35 }}>
                                  {ln.text}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    );
                  })()}
                </Box>
              )}

              {/* ═══ PILLARS ═══ */}
              <Box sx={{ mb: 2 }}>
                <SectionLabel right={fails.length > 0 ? `${fails.length} ${t('stockStrength.drawer.fails')}` : null}>
                  {t('stockStrength.drawer.componentBreakdown')}
                </SectionLabel>
                {compRows.map(r => {
                  const color = PILLAR_LEVEL_COLOR[r.levelKey] || '#94a3b8';
                  const lvlLabel = r.levelKey ? t(`stockStrength.drawer.pillarLevel.${r.levelKey}`) : '';
                  return (
                    <Box key={r.key} sx={{ display: 'grid', gridTemplateColumns: '78px 1fr 70px', alignItems: 'center', gap: 1, mb: 0.55 }}>
                      <Typography sx={{ fontSize: '0.7rem', color: '#475569', fontWeight: 600 }}>{r.label}</Typography>
                      <Box sx={{ position: 'relative', height: 7, bgcolor: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
                        <Box sx={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${Math.max(0, Math.min(100, r.val || 0))}%`, bgcolor: color, borderRadius: 4, transition: 'width 0.4s ease' }} />
                      </Box>
                      <Box sx={{ textAlign: 'right', lineHeight: 1.1 }}>
                        <Typography component="span" sx={{ fontSize: '0.72rem', color, fontWeight: 700, fontFamily: 'monospace' }}>
                          {r.val != null ? fmtDec(r.val, 0) : '—'}
                        </Typography>
                        {lvlLabel && (
                          <Typography sx={{ fontSize: '0.55rem', color, fontWeight: 600, lineHeight: 1 }}>{lvlLabel}</Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })}
                {(() => {
                  const r = c.risk_penalty;
                  const rc = riskColor(riskMeta.level_key);
                  const riskLbl = riskMeta.level_key ? t(`stockStrength.drawer.riskLevel.${riskMeta.level_key}`) : '';
                  return (
                    <Box sx={{ display: 'grid', gridTemplateColumns: '78px 1fr 70px', alignItems: 'center', gap: 1, mt: 0.8, pt: 0.6, borderTop: '1px dashed #e2e8f0' }}>
                      <Typography sx={{ fontSize: '0.7rem', color: '#475569', fontWeight: 600 }}>{t('stockStrength.table.risk')}</Typography>
                      <Box sx={{ position: 'relative', height: 7, bgcolor: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
                        <Box sx={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${Math.max(0, Math.min(100, r || 0))}%`, bgcolor: rc, borderRadius: 4 }} />
                      </Box>
                      <Box sx={{ textAlign: 'right', lineHeight: 1.1 }}>
                        <Typography component="span" sx={{ fontSize: '0.72rem', color: rc, fontWeight: 700, fontFamily: 'monospace' }}>
                          {r != null ? `${fmtDec(r, 0)}%` : '—'}
                        </Typography>
                        {riskLbl && (
                          <Typography sx={{ fontSize: '0.55rem', color: rc, fontWeight: 600, lineHeight: 1 }}>{riskLbl}</Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })()}
                {fails.length > 0 && (
                  <Box sx={{ mt: 0.8, display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
                    {fails.map(f => (
                      <Chip key={f} size="small" label={getFailLabel(f, t)} sx={{
                        height: 18, fontSize: '0.58rem', fontWeight: 600,
                        bgcolor: alpha('#f59e0b', 0.1), color: '#92400e',
                        '& .MuiChip-label': { px: 0.7 },
                      }} />
                    ))}
                  </Box>
                )}
              </Box>

              {/* ═══ HISTORY ═══ */}
              <Box>
                {(() => {
                  const windowDays = historySummary.window_days || 0;
                  const maxS = historySummary.score_max;
                  const minS = historySummary.score_min;
                  const totalDelta = historySummary.total_delta;
                  return (
                    <>
                      <SectionLabel right={t('stockStrength.drawer.history.nDays', { n: windowDays })}>
                        {t('stockStrength.drawer.history.title')}
                      </SectionLabel>
                      {(maxS != null || totalDelta != null) && (
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                          {totalDelta != null && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, bgcolor: '#f8fafc', borderRadius: 1 }}>
                              {totalDelta >= 0
                                ? <TrendingUp sx={{ fontSize: 14, color: '#10b981' }} />
                                : <TrendingDown sx={{ fontSize: 14, color: '#ef4444' }} />}
                              <Typography
                                component="span"
                                sx={{ fontSize: '0.65rem', color: '#64748b', '& b': { color: totalDelta >= 0 ? '#10b981' : '#ef4444', fontWeight: 700 } }}
                                dangerouslySetInnerHTML={{
                                  __html: t('stockStrength.drawer.history.deltaSummary', { sign: totalDelta >= 0 ? '+' : '', delta: fmtDec(totalDelta, 0), n: windowDays }),
                                }}
                              />
                            </Box>
                          )}
                          {maxS != null && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, bgcolor: '#f8fafc', borderRadius: 1 }}>
                              <Typography
                                component="span"
                                sx={{
                                  fontSize: '0.65rem', color: '#64748b',
                                  '& b:nth-of-type(1)': { color: scoreColor(historySummary.score_max_level_key), fontWeight: 700 },
                                  '& b:nth-of-type(2)': { color: scoreColor(historySummary.score_min_level_key), fontWeight: 700 },
                                }}
                                dangerouslySetInnerHTML={{
                                  __html: t('stockStrength.drawer.history.peakBottom', { max: fmtDec(maxS, 0), min: fmtDec(minS, 0) }),
                                }}
                              />
                            </Box>
                          )}
                        </Box>
                      )}
                      {milestones.length > 0 && (
                        <Box sx={{ mb: 1.2, px: 1.2, py: 0.9, bgcolor: '#fafafa', border: '1px solid #f1f5f9', borderRadius: 1.5 }}>
                          <Typography sx={{ fontSize: '0.55rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700, mb: 0.6 }}>
                            {t('stockStrength.drawer.history.milestoneTitle')}
                          </Typography>
                          {milestones.slice().reverse().slice(0, 3).map((m, i) => {
                            const toTier = getTier(m.to);
                            return (
                              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.7, py: 0.25, borderTop: i === 0 ? 'none' : '1px dashed #eef2f7' }}>
                                <Typography sx={{ fontSize: '0.62rem', fontFamily: 'monospace', color: '#64748b', minWidth: 70 }}>{m.date}</Typography>
                                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: toTier.color }} />
                                <Typography sx={{ fontSize: '0.68rem', color: toTier.color, fontWeight: 700 }}>
                                  {t('stockStrength.drawer.history.enterTier', { tier: t('stockStrength.tiers.' + toTier.labelKey) })}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                    </>
                  );
                })()}
              </Box>
            </>
          );
        })() : (
          <Typography sx={{ py: 4, textAlign: 'center', color: '#94a3b8', fontSize: '0.8rem' }}>
            {t('stockStrength.drawer.noData', 'Chưa có dữ liệu')}
          </Typography>
        )}
      </Box>
    </Box>
  </Drawer>
);

export default TickerDrawer;
